import api, { apiBaseUrl } from './api'
import type { AuthUser, LeaguePlayer } from '../types'

export interface Session {
  authenticated: boolean
  user: AuthUser | null
  leaguePlayer: LeaguePlayer | null
}

const ANONYMOUS: Session = { authenticated: false, user: null, leaguePlayer: null }

/**
 * Loads the current session.
 *
 * @returns An anonymous session rather than throwing when signed out, so
 * callers can render public pages without special-casing 401s.
 */
export async function fetchSession(): Promise<Session> {
  try {
    const { data } = await api.get<Session>('/auth/me')
    return data.authenticated ? data : ANONYMOUS
  } catch {
    return ANONYMOUS
  }
}

/**
 * An action the server should finish once Discord hands the visitor back.
 *
 * @remarks
 * `season-signup` enters them in the upcoming season's pool and returns them to
 * the home page rather than to `/account`. `home` is a sign-in that started on
 * the homepage poll: it also returns them there, without signing them up.
 */
export type LoginIntent = 'season-signup' | 'home'

/**
 * Full-page redirect; the OAuth handshake cannot run through XHR.
 *
 * @param intent - What the visitor was doing when the sign-in interrupted them.
 * Omit for a plain sign-in, which clears any intent left over from an
 * abandoned one.
 */
export function startDiscordLogin(intent?: LoginIntent): void {
  const query = intent ? `?intent=${intent}` : ''
  window.location.href = `${apiBaseUrl()}/auth/discord/start${query}`
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout')
}
